import React from 'react';
import '../../App.scss';
import 'antd/dist/antd.css';
import { DollarCircleFilled, EuroCircleFilled, PoundCircleFilled , EditOutlined, EllipsisOutlined, SettingOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Avatar, List, Button, Popconfirm, Card , Table, Tag } from 'antd';
import moment from 'moment';

import {ccyIsoCodeToSymbolMap} from '../../utils/CurrencyUtils';
import { capitalize } from '../../utils/ProductSwitcher';
import { WithdrawalProgressListView } from '../client/PayoutProgressListView';

const ccy = ccyIsoCodeToSymbolMap;

const ccy_icons = {
    'EUR': <EuroCircleFilled style={{ fontSize: '30px', color: '#00c' }} />,
    'USD': <DollarCircleFilled style={{ fontSize: '30px', color: '#08c' }} />,
    'GBP': <PoundCircleFilled style={{ fontSize: '30px', color: '#70c' }} />,
};

function iconForCCY(ccy) {
    let icon = ccy_icons[ccy];
    return (icon) ? icon : <DollarCircleFilled style={{ fontSize: '30px', color: '#08c' }} />;
}

export const AdminPayoutCard = ({ isAdmin, loading, type, payout, clientId, payoutId, locStrings }) => {
    if (!payout) return null;

    // Build address string safely
    const addressParts = [];
    if (payout.holderName) addressParts.push(payout.holderName);
    
    if (payout.address) {
        if (payout.address["address-line-one"]) addressParts.push(payout.address["address-line-one"]);
        if (payout.address["address-line-two"]) addressParts.push(payout.address["address-line-two"]);
        if (payout.address["town"]) addressParts.push(payout.address["town"]);
        if (payout.address["county"]) addressParts.push(payout.address["county"]);
        if (payout.address["country"]) addressParts.push(payout.address["country"]);
    }
    const addressString = addressParts.length > 0 ? addressParts.join(', ') : null;

    // Build bank info string safely
    const bankParts = [];
    if (payout.bankName) bankParts.push(payout.bankName);
    if (payout.country) bankParts.push(`(${payout.country})`);
    if (payout.iban) bankParts.push(payout.iban);
    const bankString = bankParts.length > 0 ? bankParts.join(' ') : null;

    return (
        <Card
            loading={loading}
            style={{ marginTop: 16, backgroundColor: '#e6f7ff' }}
            type={type || "inner"}
            title="Payout Details"
        >
            <div style={{ lineHeight: '1.8' }}>
                {payout.displayName && (
                    <p style={{ margin: 0, fontWeight: 'bold', fontSize: '18px' }}>{payout.displayName}</p>
                )}
                {addressString && (
                    <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>{addressString}</p>
                )}
                {bankString && (
                    <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>{bankString}</p>
                )}
            </div>
        </Card>
    );
};

export const AdminClientCard = ({ isAdmin, loading, type, client, clientId, locStrings }) => {
    if (!client) return null;

    const description = client.email || client.phoneNumber || client.profileBio || null;

    return (
        <Card
            loading={loading}
            style={{ marginTop: 16, backgroundColor: '#fff0f6' }}
            type={type || "inner"}
            title="Client Details"
        >
            <Card.Meta 
                avatar={<Avatar src={client.avatar} />}
                title={client.displayName || 'N/A'}
                description={description}
            />
        </Card>
    );
};

export const AdminAccountCard = ({ isAdmin, loading, type, account, clientId, payoutId, locStrings }) => {
    if (!account) return null;

    return (
        <Card
            loading={loading}
            style={{ marginTop: 16, backgroundColor: '#f0f5ff' }}
            type={type || "inner"}
            title="Account Details"
        >
            <Card.Meta 
                avatar={iconForCCY(account.currency)}
                title={capitalize(account.product) || 'N/A'}
                description={capitalize(account.type) || 'N/A'}
            />
        </Card>
    );
};

export const AdminWithdrawalTitle = ({ isAdmin, withdrawal, locStrings }) => {
    if (!withdrawal || !withdrawal.client) return null;

    const amount = (withdrawal.payoutAmountCents / 100).toFixed(2);
    const displayAmount = `${ccy[withdrawal.currency] || '$'}${amount}`;
    const clientName = withdrawal.client.displayName || 'Unknown';

    return (
        <div>
            <p style={{ fontSize: "18px", fontWeight: '600', margin: 0 }}>{`${clientName} requested ${displayAmount}`}</p>
        </div>
    );
};


export const AdminWithdrawalContent = ({ isAdmin, loading, withdrawal, locStrings }) => {
    if(loading || !withdrawal) return null;

    const amountPayout = withdrawal.amount || 0;
    const amount = (amountPayout / 100).toFixed(2);
    const displayAmount = `${ccy[withdrawal.currency] || '$'}${amount}`;
    const id = withdrawal.withdrawalId || withdrawal.uid || withdrawal.id || 'N/A';
    const clientId = withdrawal.clientId;

    return (
        <div style={{ padding: '16px 0' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                <p style={{ fontSize: '36px', fontWeight: '600', color: '#ff0098', margin: '0 0 8px 0' }}>{displayAmount}</p>
                {id !== 'N/A' && (
                    <p style={{ fontSize: '13px', color: '#52c41a', fontFamily: 'monospace' }}>{id}</p>
                )}
            </div>
            <div style={{ textAlign: 'start' }}>
                <WithdrawalProgressListView
                    clientId={clientId}
                    withdrawalId={id}
                    isAdmin={isAdmin}
                />
            </div>
        </div>
    );
};


const AdminWithdrawalCard = ({ isAdmin, loading, withdrawal, locStrings, onMarkPaid, onRefund, contentCard: ContentCard, payoutCard: PayoutCard, clientCard: ClientCard, accountCard: AccountCard }) => {
    if (!withdrawal) return null;

    const actions = [];

    if(isAdmin) {
        actions.push(
            (<Button key="mark-paid" type='primary' size='small' disabled={loading} onClick={() => onMarkPaid && onMarkPaid(withdrawal)}>Mark Paid</Button>)
        );
        actions.push(
            (<Popconfirm
                key="refund"
                title="Refund Withdrawal - Super Admin Action"
                description="This is a super admin action. Make sure to check with tech team before saying yes!"
                onConfirm={() => onRefund && onRefund(withdrawal)}
                okText="Yes, Refund"
                okType="danger"
                cancelText="Cancel"
            >
                <Button type='danger' size='small' disabled={loading}>Refund</Button>
            </Popconfirm>)
        );
    }

    return (
        <Card
            style={{ maxWidth: '800px', margin: '0 auto' }}
            title={<AdminWithdrawalTitle isAdmin={isAdmin} withdrawal={withdrawal} locStrings={locStrings} />}
            actions={actions}
        >
            <ClientCard isAdmin={isAdmin} loading={loading} type="inner" client={withdrawal.client} clientId={withdrawal.clientId} locStrings={locStrings} />
            <PayoutCard isAdmin={isAdmin} loading={loading} type="inner" payout={withdrawal.payout} clientId={withdrawal.clientId} payoutId={withdrawal.payoutId} locStrings={locStrings} />
            <AccountCard isAdmin={isAdmin} loading={loading} type="inner" account={withdrawal.account} clientId={withdrawal.clientId} payoutId={withdrawal.payoutId} locStrings={locStrings} />
            <ContentCard isAdmin={isAdmin} loading={loading} type="inner" withdrawal={withdrawal} clientId={withdrawal.clientId} locStrings={locStrings} />
        </Card>
    );
};

const AdminWithdrawalIdDetails = ({ isAdmin, withdrawal, locStrings }) => {

    const withdrawalId = withdrawal.withdrawalId ? (<Tag>{withdrawal.withdrawalId}</Tag>) : null;
    const payoutId = withdrawal.payoutId ? (<Tag>{withdrawal.payoutId}</Tag>) : null;

    return (
        <div>
            <p>{`Withdrawals are processed via Revolut for Business`}</p>
            <p>
                {withdrawalId}
                {payoutId}
            </p>
        </div>
    );

};


export const AdminWithdrawalDetail = ({ loading, locStrings, withdrawal, onMarkPaid, onRefund, isAdmin, ...rest }) => {

    return (
        <AdminWithdrawalCard
            loading={loading}
            withdrawal={withdrawal}
            locStrings={locStrings}
            payoutCard={AdminPayoutCard}
            clientCard={AdminClientCard}
            accountCard={AdminAccountCard}
            contentCard={AdminWithdrawalContent}
            onMarkPaid={onMarkPaid}
            onRefund={onRefund}
            isAdmin={isAdmin}
        />
    );

};
